import { useCallback } from "react";
import {
  fetchProviderMigrationConnection,
  fetchProviderMigrationInventoryPage,
  type ApiDetailResponse,
  type ApiErrorInfo,
  type ApiProviderMigrationConnection,
  type ApiProviderMigrationInventoryItem,
  type ProviderMigrationCategory,
} from "@/lib/api";
import { useApiQuery } from "@/hooks/use-api";

/** One sealed-snapshot inventory read, paged through to completion (bounded). */
export interface ProviderMigrationInventory {
  category: ProviderMigrationCategory;
  snapshotId: string | null;
  freshUntil: string | null;
  items: ApiProviderMigrationInventoryItem[];
  /** True when the page cap was hit: the list is NOT the complete inventory and is labelled so. */
  truncated: boolean;
}

const PAGE_SIZE = 500;
const MAX_PAGES = 10;

const EMPTY_CONNECTION: ApiProviderMigrationConnection | null = null;

export function useProviderMigrationConnection() {
  return useApiQuery<ApiDetailResponse<ApiProviderMigrationConnection>, ApiProviderMigrationConnection | null>(
    // Pass the module-level function, not an inline arrow: useApiQuery re-runs its fetch whenever
    // the fetcher's identity changes, so an inline arrow would refetch on every render.
    fetchProviderMigrationConnection,
    mapConnection,
    { initialData: EMPTY_CONNECTION },
  );
}

function mapConnection(raw: ApiDetailResponse<ApiProviderMigrationConnection>): {
  data: ApiProviderMigrationConnection | null;
  error?: ApiErrorInfo;
} {
  return { data: raw.item, error: raw.error };
}

async function readAllPages(
  category: ProviderMigrationCategory,
): Promise<ApiDetailResponse<ProviderMigrationInventory>> {
  const items: ApiProviderMigrationInventoryItem[] = [];
  let cursor: string | null = null;
  let snapshotId: string | null = null;
  let freshUntil: string | null = null;
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const result = await fetchProviderMigrationInventoryPage(category, PAGE_SIZE, cursor);
    if (result.error || !result.item) return { item: null, error: result.error ?? { reasonCode: "EMPTY_RESPONSE" } };
    // Every page must come from the same sealed snapshot. If the snapshot changes mid-read, the
    // pages cannot be mixed under one id: fail the read and let the operator retry.
    if (snapshotId !== null && result.item.snapshotId !== snapshotId) {
      return { item: null, error: { reasonCode: "SNAPSHOT_CHANGED_DURING_READ" } };
    }
    snapshotId = result.item.snapshotId;
    freshUntil = result.item.freshUntil ?? null;
    items.push(...result.item.items);
    cursor = result.item.nextCursor ?? null;
    if (!cursor) return { item: { category, snapshotId, freshUntil, items, truncated: false } };
  }
  return { item: { category, snapshotId, freshUntil, items, truncated: true } };
}

function mapInventory(raw: ApiDetailResponse<ProviderMigrationInventory>): {
  data: ProviderMigrationInventory | null;
  error?: ApiErrorInfo;
} {
  return { data: raw.item, error: raw.error };
}

const NO_INVENTORY: ProviderMigrationInventory | null = null;

export function useProviderMigrationInventory(category: ProviderMigrationCategory) {
  const fetcher = useCallback(() => readAllPages(category), [category]);
  return useApiQuery<ApiDetailResponse<ProviderMigrationInventory>, ProviderMigrationInventory | null>(
    fetcher,
    mapInventory,
    { initialData: NO_INVENTORY },
  );
}

/** The three read categories together, for the summary pages (import review, evidence, disposition). */
export function useProviderMigrationSummary() {
  const vaults = useProviderMigrationInventory("VAULT_ACCOUNTS");
  const wallets = useProviderMigrationInventory("ASSET_WALLETS");
  const addresses = useProviderMigrationInventory("VAULT_ADDRESSES");
  const results = [vaults, wallets, addresses];
  const loading = results.some((r) => r.loading);
  // The summary pages present one snapshot. Categories read from different sealed snapshots are
  // never combined under a single id; that is surfaced as an error, not silently merged.
  const snapshotIds = new Set(results.map((r) => r.data?.snapshotId).filter(Boolean));
  const mismatch: ApiErrorInfo | null = snapshotIds.size > 1 ? { reasonCode: "SNAPSHOT_MISMATCH" } : null;
  const error = results.find((r) => r.error)?.error ?? mismatch;
  const total = results.reduce((sum, r) => sum + (r.data?.items.length ?? 0), 0);
  const truncated = results.some((r) => r.data?.truncated);
  const snapshotId = results.map((r) => r.data?.snapshotId).find(Boolean) ?? null;
  const freshUntil = results.map((r) => r.data?.freshUntil).find(Boolean) ?? null;
  const refresh = () => results.forEach((r) => r.refresh());
  return { vaults, wallets, addresses, loading, error, total, truncated, snapshotId, freshUntil, refresh };
}
